import { Field, inputClass } from "../routes/SignIn.js";
import { ServiceAreaMap } from "../serviceArea/ServiceAreaMap.js";
import { BellIcon, Card, MapPinIcon, PrimaryButton, SlidersIcon, Toggle } from "./ui.js";

/**
 * Step 6 — lead preferences. Geography is the matchable part (ZIPs expanded
 * from the towns picked on the map), plus how leads are delivered and the
 * business rules that shape which jobs reach the pro.
 */
export interface LeadPrefs {
  selectedTowns: string[];
  leadDelivery: { email: boolean; sms: boolean };
  businessRules: {
    minJobSize: string;
    freeEstimates: boolean;
    emergencyCalls: boolean;
    residential: boolean;
    commercial: boolean;
  };
}

export function PreferencesStep({
  prefs,
  onChange,
  onFinish,
  submitting,
  error,
}: {
  prefs: LeadPrefs;
  onChange: (patch: Partial<LeadPrefs>) => void;
  onFinish: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const { businessRules: rules } = prefs;
  const setRule = (patch: Partial<LeadPrefs["businessRules"]>) =>
    onChange({ businessRules: { ...rules, ...patch } });

  return (
    <main className="mx-auto max-w-md px-5 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-charcoal">Where &amp; how you get leads</h1>
        <p className="mt-2 text-sm text-charcoal">
          Last step — set your coverage area and the jobs you want.
        </p>
      </header>

      <div className="space-y-5">
        <Card icon={<MapPinIcon />} title="Coverage area">
          <p className="text-sm text-charcoal">
            Brush a radius around your base, then tap towns to fine-tune.
          </p>
          <ServiceAreaMap
            selected={prefs.selectedTowns}
            onChange={(selectedTowns) => onChange({ selectedTowns })}
          />
        </Card>

        <Card icon={<BellIcon />} title="How to reach you">
          <Toggle
            label="Email me new leads"
            checked={prefs.leadDelivery.email}
            onChange={(email) => onChange({ leadDelivery: { ...prefs.leadDelivery, email } })}
          />
          <Toggle
            label="Text me new leads"
            hint="Sent to your mobile number."
            checked={prefs.leadDelivery.sms}
            onChange={(sms) => onChange({ leadDelivery: { ...prefs.leadDelivery, sms } })}
          />
        </Card>

        <Card icon={<SlidersIcon />} title="Job preferences">
          <Field label="Minimum job size (optional)">
            <input
              className={inputClass}
              value={rules.minJobSize}
              onChange={(e) => setRule({ minJobSize: e.target.value })}
              placeholder="e.g. $250"
            />
          </Field>
          <Toggle
            label="Residential jobs"
            checked={rules.residential}
            onChange={(residential) => setRule({ residential })}
          />
          <Toggle
            label="Commercial jobs"
            checked={rules.commercial}
            onChange={(commercial) => setRule({ commercial })}
          />
          <Toggle
            label="I offer free estimates"
            checked={rules.freeEstimates}
            onChange={(freeEstimates) => setRule({ freeEstimates })}
          />
          <Toggle
            label="Accept emergency calls"
            checked={rules.emergencyCalls}
            onChange={(emergencyCalls) => setRule({ emergencyCalls })}
          />
        </Card>
      </div>

      {error && (
        <p role="alert" className="mt-5 text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="mt-8">
        <PrimaryButton onClick={onFinish} disabled={submitting}>
          {submitting ? "Finishing up…" : "Finish & submit"}
        </PrimaryButton>
      </div>
    </main>
  );
}
